<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Activos Fijos"
      v-model="filtro"
      searchPlaceholder="Buscar por artículo o # serie..."
    >
      <template #actions>
        <button class="btn btn-exportar btn-header" @click="exportarExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span class="btn-header-texto ms-2">Exportar Excel</span>
        </button>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-plus-circle-fill"></i>
          <span class="btn-header-texto ms-2">Nuevo activo</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- ── Tabla escritorio ───────────────────────────────────── -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4 col-articulo">
              <ColumnFilterMenu
                field="articulo"
                label="Artículo"
                :filterable="false"
                :items="activosParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="numero_serie"
                label="# Serie"
                :filterable="false"
                :items="activosParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="categoria_nombre"
                label="Categoría"
                :items="activosParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                :modelValue="filtroCategoriaSet"
                @update:modelValue="filtroCategoriaSet = $event"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="area_nombre"
                label="Área"
                :filterable="false"
                :items="activosParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="responsable"
                label="Responsable"
                :filterable="false"
                :items="activosParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="responsiva_estado"
                label="Responsiva"
                :sortable="false"
                :items="activosParaFiltro"
                :modelValue="filtroResponsivaSet"
                @update:modelValue="filtroResponsivaSet = $event"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="activo in activosPaginados" :key="activo.id">
            <td class="ps-4 col-articulo-td">
              <span class="fw-bold text-cafe articulo-texto">{{ activo.articulo }}</span>
            </td>
            <td>
              <span class="badge-serie">{{ activo.numero_serie || "—" }}</span>
            </td>
            <td>
              <span class="badge-categoria">{{ activo.categoria_nombre }}</span>
            </td>
            <td>
              <span v-if="activo.area_nombre" class="text-cafe">
                <i class="bi bi-map me-1 text-verde"></i>{{ activo.area_nombre }}
              </span>
              <span v-else class="text-muted-divisan">—</span>
            </td>
            <td>
              <span v-if="activo.responsable" class="text-cafe">
                <i class="bi bi-person-fill me-1 text-verde"></i>{{ activo.responsable }}
              </span>
              <span v-else class="text-muted-divisan">—</span>
            </td>
            <td class="text-center">
              <span :class="['badge-doc', claseResponsivaEstado(activo.responsiva_estado)]">
                <i
                  :class="
                    activo.responsiva_estado === 'Completa'
                      ? 'bi bi-file-earmark-check-fill me-1'
                      : activo.responsiva_estado === 'Faltante'
                      ? 'bi bi-exclamation-triangle-fill me-1'
                      : 'bi bi-dash-circle me-1'
                  "
                ></i>
                {{ activo.responsiva_estado }}
              </span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button
                  class="btn-circular"
                  @click="abrirDetalle(activo)"
                  title="Ver detalle"
                >
                  <i class="bi bi-eye"></i>
                </button>
                <button class="btn-circular" @click="abrirModal(activo)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarEliminar(activo)"
                  title="Eliminar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="activosPaginados.length === 0">
            <td colspan="7" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron resultados
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de
            {{ activosFiltrados.length }} activos</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in opcionesPorPagina" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Cards móvil ────────────────────────────────────────── -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="activo in activosPaginados"
        :key="'mob-' + activo.id"
        class="divisan-mobile-card mb-2 px-3 py-2 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="fw-bold text-cafe">{{ activo.articulo }}</span>
          <div class="d-flex gap-2">
            <button
              class="btn-circular-sm"
              @click="abrirDetalle(activo)"
              title="Ver detalle"
            >
              <i class="bi bi-eye text-cafe"></i>
            </button>
            <button class="btn-circular-sm" @click="abrirModal(activo)" title="Editar">
              <i class="bi bi-pencil-square text-verde"></i>
            </button>
            <button
              class="btn-circular-sm"
              @click="confirmarEliminar(activo)"
              title="Eliminar"
            >
              <i class="bi bi-trash3-fill text-danger"></i>
            </button>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
          <span class="badge-categoria">{{ activo.categoria_nombre }}</span>
          <span v-if="activo.numero_serie" class="badge-serie">{{
            activo.numero_serie
          }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <small class="text-cafe fw-bold">{{
            formatCurrency(activo.costo_total)
          }}</small>
          <small class="text-muted-divisan"
            >· {{ activo.cantidad }} u. ·
            {{ formatFecha(activo.fecha_adquisicion) }}</small
          >
          <span v-if="activo.tiene_responsiva" class="badge-doc ok"
            ><i class="bi bi-file-earmark-check-fill me-1"></i>Resp.</span
          >
          <span v-else-if="activo.requiere_responsiva" class="badge-doc faltante"
            ><i class="bi bi-exclamation-triangle-fill me-1"></i>Faltante</span
          >
        </div>
      </div>

      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ activosFiltrados.length }}</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in opcionesPorPagina" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         MODAL DETALLE
    ══════════════════════════════════════════════════════════ -->
    <div
      class="modal fade"
      id="modalDetalleActivo"
      tabindex="-1"
      ref="modalDetalleElement"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              <i class="bi bi-eye me-2"></i>Detalle del activo
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="detalleLoading" class="text-center py-4">
              <div class="spinner-border text-verde"></div>
            </div>
            <template v-else-if="detalle">
              <!-- Fila 1: Artículo (ancho completo) -->
              <div class="det-card det-card-full mb-3">
                <div class="det-card-label">Artículo</div>
                <div class="det-card-valor fw-bold fs-6">{{ detalle.articulo }}</div>
              </div>

              <!-- Fila 2: Serie + Categoría -->
              <div class="det-grid-2 mb-3">
                <div class="det-card">
                  <div class="det-card-label"># Serie</div>
                  <div class="det-card-valor">
                    <span v-if="detalle.numero_serie" class="badge-serie">{{
                      detalle.numero_serie
                    }}</span>
                    <span v-else class="text-muted-divisan">—</span>
                  </div>
                </div>
                <div class="det-card">
                  <div class="det-card-label">Categoría</div>
                  <div class="det-card-valor">
                    <span class="badge-categoria">{{ detalle.categoria_nombre }}</span>
                    <span
                      v-if="detalle.requiere_responsiva"
                      class="d-block mt-1 badge-doc ok"
                      style="font-size: 0.7rem; width: fit-content"
                    >
                      <i class="bi bi-file-earmark-check-fill me-1"></i>Requiere
                      responsiva
                    </span>
                  </div>
                </div>
              </div>

              <!-- Fila 2b: Área + Responsable -->
              <div class="det-grid-2 mb-3">
                <div class="det-card">
                  <div class="det-card-label">Área de trabajo</div>
                  <div class="det-card-valor">
                    <span v-if="detalle.area_nombre">
                      <i class="bi bi-map me-1 text-verde"></i>{{ detalle.area_nombre }}
                    </span>
                    <span v-else class="text-muted-divisan">Sin área asignada</span>
                  </div>
                </div>
                <div class="det-card">
                  <div class="det-card-label">Responsable</div>
                  <div class="det-card-valor">
                    <span v-if="detalle.responsable">
                      <i class="bi bi-person-fill me-1 text-verde"></i
                      >{{ detalle.responsable }}
                    </span>
                    <span v-else class="text-muted-divisan">Sin responsable</span>
                  </div>
                </div>
              </div>

              <!-- Fila 3: Fecha + Cantidad + Costo unit + Costo total -->
              <div class="det-grid-4 mb-3">
                <div class="det-card">
                  <div class="det-card-label">F. Adquisición</div>
                  <div class="det-card-valor fw-bold">
                    {{ formatFecha(detalle.fecha_adquisicion) }}
                  </div>
                </div>
                <div class="det-card">
                  <div class="det-card-label">Piezas</div>
                  <div class="det-card-valor fw-bold">{{ detalle.cantidad }}</div>
                </div>
                <div class="det-card">
                  <div class="det-card-label">Costo unit.</div>
                  <div class="det-card-valor">
                    {{ formatCurrency(detalle.costo_unitario) }}
                  </div>
                </div>
                <div class="det-card det-card-destacado">
                  <div class="det-card-label">Costo total</div>
                  <div class="det-card-valor fw-bold text-verde">
                    {{ formatCurrency(detalle.costo_total) }}
                  </div>
                </div>
              </div>

              <!-- Folio fiscal (si existe) -->
              <div class="det-card det-card-full mb-3" v-if="detalle.folio_fiscal">
                <div class="det-card-label">Folio fiscal</div>
                <div class="det-card-valor folio-fiscal-text">
                  {{ detalle.folio_fiscal }}
                </div>
              </div>

              <!-- Notas (si existen) -->
              <div class="det-card det-card-full mb-3" v-if="detalle.notas">
                <div class="det-card-label">Notas</div>
                <div class="det-card-valor">{{ detalle.notas }}</div>
              </div>

              <!-- Documentos -->
              <div class="det-card-label mb-2">Documentos</div>
              <div class="docs-grid">
                <div
                  class="doc-card"
                  :class="
                    detalle.tiene_responsiva
                      ? 'doc-ok'
                      : detalle.requiere_responsiva
                      ? 'doc-faltante'
                      : 'doc-na'
                  "
                >
                  <i class="bi bi-file-earmark-person doc-icon"></i>
                  <div class="doc-titulo">Responsiva</div>
                  <div class="doc-estado">
                    <a
                      v-if="detalle.tiene_responsiva"
                      :href="detalle.responsiva_url"
                      target="_blank"
                      class="btn-doc-link"
                    >
                      <i class="bi bi-box-arrow-up-right me-1"></i>Abrir PDF
                    </a>
                    <span
                      v-else-if="detalle.requiere_responsiva"
                      class="text-danger small fw-bold"
                    >
                      <i class="bi bi-exclamation-triangle-fill me-1"></i>Faltante
                    </span>
                    <span v-else class="text-muted-divisan small">No requerida</span>
                  </div>
                </div>
                <div
                  class="doc-card"
                  :class="detalle.tiene_factura ? 'doc-ok' : 'doc-na'"
                >
                  <i class="bi bi-receipt doc-icon"></i>
                  <div class="doc-titulo">Factura</div>
                  <div class="doc-estado">
                    <a
                      v-if="detalle.tiene_factura"
                      :href="detalle.factura_url"
                      target="_blank"
                      class="btn-doc-link"
                    >
                      <i class="bi bi-box-arrow-up-right me-1"></i>Abrir PDF
                    </a>
                    <span v-else class="text-muted-divisan small">No adjuntada</span>
                  </div>
                </div>
                <div
                  class="doc-card"
                  :class="detalle.imagenes?.length ? 'doc-ok' : 'doc-na'"
                >
                  <i class="bi bi-images doc-icon"></i>
                  <div class="doc-titulo">Fotos</div>
                  <div class="doc-estado">
                    <button
                      v-if="detalle.imagenes?.length"
                      type="button"
                      class="btn-doc-link btn-doc-link-plain"
                      @click="abrirModalImagenesDetalle"
                    >
                      <i class="bi bi-eye me-1"></i>Ver ({{ detalle.imagenes.length }})
                    </button>
                    <span v-else class="text-muted-divisan small">Sin fotos</span>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-cancelar px-4" data-bs-dismiss="modal">
              Cerrar
            </button>
            <button
              type="button"
              class="btn btn-nuevo px-4"
              @click="
                abrirModal(detalleActivoRef);
                modalDetalleInstance.hide();
              "
            >
              <i class="bi bi-pencil-square me-1"></i>Editar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         MODAL CREAR / EDITAR
    ══════════════════════════════════════════════════════════ -->
    <div
      class="modal fade"
      id="modalActivoFijo"
      tabindex="-1"
      aria-hidden="true"
      ref="modalElement"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              <i class="bi bi-building-gear me-2"></i>
              {{ editando ? "Editar activo" : "Nuevo activo" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>

          <form @submit.prevent="guardarActivo">
            <div class="modal-body">
              <div class="row g-3">
                <!-- Artículo -->
                <div class="col-12 col-md-8">
                  <label class="form-label label-divisan"
                    >Artículo <span class="text-danger">*</span></label
                  >
                  <input
                    v-model="form.articulo"
                    type="text"
                    class="form-control divisan-input-modal"
                    required
                    placeholder="Ej. Laptop Dell Latitude 5540"
                    maxlength="255"
                  />
                </div>

                <!-- Número de serie -->
                <div class="col-12 col-md-4">
                  <label class="form-label label-divisan"># Serie</label>
                  <input
                    v-model="form.numero_serie"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Ej. SN-2024-001"
                    maxlength="100"
                  />
                </div>

                <!-- Categoría -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan"
                    >Categoría <span class="text-danger">*</span></label
                  >
                  <select
                    v-model="form.categoria"
                    class="form-select divisan-input-modal"
                    required
                  >
                    <option value="">Selecciona una categoría</option>
                    <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                      {{ cat.nombre }}
                    </option>
                  </select>
                </div>

                <!-- Área de trabajo -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">
                    Área de trabajo <span class="text-muted-divisan">(opcional)</span>
                  </label>
                  <div class="area-dropdown-wrap">
                    <div
                      class="area-dropdown-trigger divisan-input-modal"
                      :class="{ 'area-open': areaDropdownAbierto }"
                      @click="areaDropdownAbierto = !areaDropdownAbierto"
                    >
                      <span v-if="form.area" class="area-selected-text">
                        <i class="bi bi-map me-2 text-verde"></i>
                        {{ areas.find((a) => a.id === form.area)?.nombre }}
                      </span>
                      <span v-else class="area-placeholder">Sin área asignada</span>
                      <i
                        class="bi ms-auto"
                        :class="areaDropdownAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                      ></i>
                    </div>
                    <div v-if="areaDropdownAbierto" class="area-dropdown-list shadow-sm">
                      <div
                        class="area-dropdown-item area-item-none"
                        :class="{ 'area-item-activa': !form.area }"
                        @click="
                          form.area = null;
                          areaDropdownAbierto = false;
                        "
                      >
                        <i class="bi bi-x-circle me-2"></i>Sin área asignada
                      </div>
                      <div
                        v-for="a in areas"
                        :key="a.id"
                        class="area-dropdown-item"
                        :class="{ 'area-item-activa': form.area === a.id }"
                        @click="
                          form.area = a.id;
                          areaDropdownAbierto = false;
                        "
                      >
                        <i class="bi bi-map me-2"></i>{{ a.nombre }}
                      </div>
                      <div v-if="areas.length === 0" class="area-dropdown-empty">
                        No hay áreas registradas
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Responsable -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">
                    Responsable <span class="text-muted-divisan">(opcional)</span>
                  </label>
                  <div class="input-icon-wrap">
                    <i class="bi bi-person input-icon"></i>
                    <input
                      v-model="form.responsable"
                      type="text"
                      class="form-control divisan-input-modal input-con-icon"
                      placeholder="Nombre completo del responsable"
                      maxlength="255"
                    />
                  </div>
                </div>

                <!-- Fecha adquisición -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan"
                    >Fecha de adquisición <span class="text-danger">*</span></label
                  >
                  <input
                    v-model="form.fecha_adquisicion"
                    type="date"
                    class="form-control divisan-input-modal"
                    required
                  />
                </div>

                <!-- Cantidad -->
                <div class="col-6 col-md-4">
                  <label class="form-label label-divisan"
                    >Cantidad <span class="text-danger">*</span></label
                  >
                  <input
                    v-model.number="form.cantidad"
                    type="number"
                    class="form-control divisan-input-modal"
                    required
                    min="1"
                  />
                </div>

                <!-- Costo unitario -->
                <div class="col-6 col-md-4">
                  <label class="form-label label-divisan"
                    >Costo unitario <span class="text-danger">*</span></label
                  >
                  <div class="input-group">
                    <span class="input-group-text input-prefix">$</span>
                    <input
                      v-model.number="form.costo_unitario"
                      type="number"
                      class="form-control divisan-input-modal"
                      required
                      min="0.01"
                      step="0.01"
                    />
                  </div>
                </div>

                <!-- Costo total -->
                <div class="col-12 col-md-4">
                  <label class="form-label label-divisan">Costo total</label>
                  <div class="costo-total-display">
                    {{ formatCurrency(costoTotalCalculado) }}
                  </div>
                </div>

                <!-- Alerta responsiva -->
                <div class="col-12" v-if="categoriaRequiereResponsiva">
                  <div class="alerta-responsiva">
                    <i class="bi bi-info-circle-fill me-2"></i>
                    La categoría
                    <strong>{{ categoriaSeleccionada?.nombre }}</strong> requiere
                    responsiva PDF.
                  </div>
                </div>

                <!-- Responsiva PDF -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">
                    Responsiva PDF
                    <span v-if="categoriaRequiereResponsiva" class="text-danger">*</span>
                    <span v-else class="text-muted-divisan">(opcional)</span>
                  </label>
                  <div
                    v-if="editando && form.responsiva_url && !form.reemplazarResponsiva"
                    class="doc-actual mb-2"
                  >
                    <i class="bi bi-file-earmark-check-fill text-verde me-2"></i>
                    <a
                      :href="form.responsiva_url"
                      target="_blank"
                      class="text-cafe fw-bold"
                      >Ver actual</a
                    >
                    <button
                      type="button"
                      class="btn-reemplazar ms-3"
                      @click="form.reemplazarResponsiva = true"
                    >
                      <i class="bi bi-arrow-repeat me-1"></i>Reemplazar
                    </button>
                  </div>
                  <input
                    v-if="!editando || !form.responsiva_url || form.reemplazarResponsiva"
                    type="file"
                    class="form-control divisan-input-modal"
                    accept=".pdf"
                    :required="categoriaRequiereResponsiva && !form.responsiva_url"
                    @change="onResponsivaChange"
                    ref="inputResponsiva"
                  />
                  <small class="text-muted-divisan"
                    ><i class="bi bi-paperclip me-1"></i>Solo PDF.</small
                  >
                </div>

                <!-- Factura PDF -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">
                    Factura PDF <span class="text-muted-divisan">(opcional)</span>
                  </label>
                  <div
                    v-if="editando && form.factura_url && !form.reemplazarFactura"
                    class="doc-actual mb-2"
                  >
                    <i class="bi bi-file-earmark-check-fill text-verde me-2"></i>
                    <a :href="form.factura_url" target="_blank" class="text-cafe fw-bold"
                      >Ver actual</a
                    >
                    <button
                      type="button"
                      class="btn-reemplazar ms-3"
                      @click="form.reemplazarFactura = true"
                    >
                      <i class="bi bi-arrow-repeat me-1"></i>Reemplazar
                    </button>
                  </div>
                  <input
                    v-if="!editando || !form.factura_url || form.reemplazarFactura"
                    type="file"
                    class="form-control divisan-input-modal"
                    accept=".pdf"
                    @change="onFacturaChange"
                    ref="inputFactura"
                  />
                  <small class="text-muted-divisan"
                    ><i class="bi bi-paperclip me-1"></i>Solo PDF.</small
                  >
                </div>

                <!-- Imágenes de evidencia -->
                <div class="col-12">
                  <label class="form-label label-divisan">
                    Fotos de evidencia <span class="text-muted-divisan">(opcional)</span>
                  </label>

                  <div v-if="imagenesVisibles.length" class="miniaturas-grid mb-2">
                    <div
                      v-for="item in imagenesVisibles"
                      :key="item.key"
                      class="miniatura-item"
                      :class="{ 'miniatura-pendiente': item.tipo === 'nueva' }"
                    >
                      <img :src="item.src" alt="Evidencia" />
                      <span v-if="item.tipo === 'nueva'" class="miniatura-badge">Nueva</span>
                      <button
                        type="button"
                        class="miniatura-eliminar"
                        title="Quitar imagen"
                        @click="eliminarMiniaturaImagen(item)"
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                  <p v-else class="text-cafe opacity-40 small mb-2">
                    Aún no hay fotos registradas.
                  </p>

                  <div
                    class="upload-zone-img"
                    :class="{ 'is-dragover': arrastrandoImagen }"
                    @click="fileRefImagen.click()"
                    @dragover.prevent="arrastrandoImagen = true"
                    @dragleave.prevent="arrastrandoImagen = false"
                    @drop.prevent="handleDropImagenes"
                  >
                    <input
                      ref="fileRefImagen"
                      type="file"
                      accept="image/*"
                      multiple
                      class="d-none"
                      @change="handleSeleccionarImagenes"
                    />
                    <i class="bi bi-cloud-arrow-up"></i>
                    <span>Agregar fotos <small>(arrastra o haz clic)</small></span>
                  </div>
                  <small class="text-muted-divisan d-block mt-1"
                    >Las fotos se guardan hasta que confirmes con el botón de abajo.</small
                  >
                </div>

                <!-- Folio fiscal -->
                <div class="col-12">
                  <label class="form-label label-divisan">
                    Folio fiscal <span class="text-muted-divisan">(opcional)</span>
                  </label>
                  <input
                    v-model="form.folio_fiscal"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Ej. 08427D02-1598-4ED8-8394-7C7CC8466338"
                    maxlength="100"
                  />
                  <small class="text-muted-divisan"
                    ><i class="bi bi-info-circle me-1"></i>UUID del folio fiscal de la
                    factura.</small
                  >
                </div>

                <!-- Notas -->
                <div class="col-12">
                  <label class="form-label label-divisan">Notas</label>
                  <textarea
                    v-model="form.notas"
                    class="form-control divisan-input-modal"
                    rows="2"
                    placeholder="Observaciones adicionales..."
                  ></textarea>
                </div>
              </div>
            </div>

            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn btn-cancelar me-2" data-bs-dismiss="modal">
                Cancelar
              </button>
              <button type="submit" class="btn btn-nuevo px-4 py-2" :disabled="guardando">
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar activo" : "Guardar activo" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         MODAL ELIMINAR
    ══════════════════════════════════════════════════════════ -->
    <div
      class="modal fade"
      id="modalEliminarActivo"
      tabindex="-1"
      aria-hidden="true"
      ref="modalEliminarElement"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-danger">
              <i class="bi bi-trash3-fill me-2"></i>Eliminar activo
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="activo-eliminar-info mb-3">
              <p class="mb-1 text-cafe fw-bold">{{ activoAEliminar?.articulo }}</p>
              <small class="text-muted-divisan">
                {{ activoAEliminar?.categoria_nombre }} ·
                {{ formatCurrency(activoAEliminar?.costo_total) }}
              </small>
            </div>
            <div class="mb-3">
              <label class="form-label label-divisan"
                >Motivo de eliminación <span class="text-danger">*</span></label
              >
              <textarea
                v-model="motivoEliminar"
                class="form-control divisan-input-modal"
                rows="3"
                placeholder="Describe el motivo..."
                required
              ></textarea>
              <small class="text-muted-divisan"
                ><i class="bi bi-info-circle me-1"></i>Este registro quedará en el
                historial.</small
              >
            </div>
          </div>
          <div class="modal-footer border-0 pt-0 d-flex gap-2">
            <button
              type="button"
              class="btn btn-cancelar flex-grow-1"
              data-bs-dismiss="modal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-eliminar flex-grow-1"
              :disabled="!motivoEliminar.trim() || eliminando"
              @click="ejecutarEliminar"
            >
              <span
                v-if="eliminando"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Confirmar eliminación
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         MODAL IMÁGENES (carrusel — solo lectura)
    ══════════════════════════════════════════════════════════ -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalImagenesElement">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <div>
              <h5 class="modal-title fw-bold text-cafe mb-0">Fotos del activo</h5>
              <small class="text-cafe opacity-60 fw-bold">{{ detalle?.articulo }}</small>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="detalle">
            <div v-if="detalle.imagenes?.length" class="carrusel-wrap">
              <div class="carrusel-viewport">
                <img
                  v-if="imagenActualDetalle"
                  :src="imagenActualDetalle.imagen"
                  :alt="`Imagen ${currentImageIndex + 1}`"
                  class="carrusel-img"
                />
                <button
                  v-if="detalle.imagenes.length > 1"
                  type="button"
                  class="carrusel-flecha carrusel-flecha-izq"
                  @click="imagenAnteriorDetalle"
                  aria-label="Anterior"
                >
                  <i class="bi bi-chevron-left"></i>
                </button>
                <button
                  v-if="detalle.imagenes.length > 1"
                  type="button"
                  class="carrusel-flecha carrusel-flecha-der"
                  @click="imagenSiguienteDetalle"
                  aria-label="Siguiente"
                >
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>
              <div class="carrusel-footer">
                <span class="carrusel-contador"
                  >{{ currentImageIndex + 1 }} / {{ detalle.imagenes.length }}</span
                >
              </div>
            </div>
            <p v-else class="text-cafe opacity-40 small mb-0 text-center py-4">
              Sin fotos registradas
            </p>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-nuevo px-4" data-bs-dismiss="modal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getCategoriasActivo,
  getAreasEmpresa,
  getActivosFijos,
  getActivoFijo,
  crearActivoFijo,
  actualizarActivoFijo,
  eliminarActivoFijo,
  exportarActivosFijos,
  subirImagenActivoFijo,
  eliminarImagenActivoFijo,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

// ── Estado general ───────────────────────────────────────────────
const activos = ref([]);
const categorias = ref([]);
const areas = ref([]);
const loading = ref(false);
const guardando = ref(false);
const eliminando = ref(false);
const editando = ref(false);
const paginaActual = ref(1);
const porPagina = ref(10);
const opcionesPorPagina = [5, 10, 15, 20];

// ── Filtros ──────────────────────────────────────────────────────
const filtro = ref("");
const filtroCategoriaSet = ref(new Set());
const filtroResponsivaSet = ref(new Set());

// ── Ordenamiento ─────────────────────────────────────────────────
const sortKey = ref("articulo");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

function estadoResponsiva(a) {
  if (a.requiere_responsiva && !a.tiene_responsiva) return "Faltante";
  if (a.tiene_responsiva) return "Completa";
  return "No requerida";
}
function claseResponsivaEstado(estado) {
  if (estado === "Completa") return "ok";
  if (estado === "Faltante") return "faltante";
  return "na";
}

// ── Modal crear/editar ───────────────────────────────────────────
const modalElement = ref(null);
const inputResponsiva = ref(null);
const inputFactura = ref(null);
const areaDropdownAbierto = ref(false);
let modalInstance = null;

const form = ref({
  id: null,
  articulo: "",
  numero_serie: "",
  categoria: "",
  area: null,
  responsable: "",
  cantidad: 1,
  costo_unitario: null,
  fecha_adquisicion: "",
  notas: "",
  folio_fiscal: "",
  responsiva_url: null,
  nueva_responsiva: null,
  reemplazarResponsiva: false,
  factura_url: null,
  nueva_factura: null,
  reemplazarFactura: false,
  imagenes: [],
  activo: true,
});

// ── Modal detalle ────────────────────────────────────────────────
const modalDetalleElement = ref(null);
let modalDetalleInstance = null;
const detalle = ref(null);
const detalleLoading = ref(false);
const detalleActivoRef = ref(null);

// ── Modal eliminar ───────────────────────────────────────────────
const modalEliminarElement = ref(null);
const activoAEliminar = ref(null);
const motivoEliminar = ref("");
let modalEliminarInstance = null;

// ── Modal imágenes — visor (solo lectura) ─────────────────────────
const modalImagenesElement = ref(null);
let modalImagenesInstance = null;
const currentImageIndex = ref(0);

const imagenActualDetalle = computed(() => {
  const imagenes = detalle.value?.imagenes || [];
  return imagenes[currentImageIndex.value] || null;
});

function abrirModalImagenesDetalle() {
  if (!detalle.value?.imagenes?.length) return;
  currentImageIndex.value = 0;
  modalImagenesInstance.show();
}

function imagenAnteriorDetalle() {
  const total = detalle.value?.imagenes?.length || 0;
  if (total === 0) return;
  currentImageIndex.value = (currentImageIndex.value - 1 + total) % total;
}

function imagenSiguienteDetalle() {
  const total = detalle.value?.imagenes?.length || 0;
  if (total === 0) return;
  currentImageIndex.value = (currentImageIndex.value + 1) % total;
}

watch([filtro, filtroCategoriaSet, filtroResponsivaSet], () => {
  paginaActual.value = 1;
});

// ── Computed ─────────────────────────────────────────────────────
const categoriaSeleccionada = computed(
  () => categorias.value.find((c) => c.id === form.value.categoria) ?? null
);
const categoriaRequiereResponsiva = computed(
  () => categoriaSeleccionada.value?.requiere_responsiva ?? false
);
const costoTotalCalculado = computed(() => {
  const c = form.value.costo_unitario;
  const q = form.value.cantidad;
  if (!c || !q) return 0;
  return c * q;
});

const activosBuscados = computed(() => {
  const s = filtro.value.toLowerCase();
  return activos.value.filter(
    (a) =>
      !s ||
      a.articulo.toLowerCase().includes(s) ||
      (a.numero_serie && a.numero_serie.toLowerCase().includes(s))
  );
});

const activosParaFiltro = computed(() =>
  activosBuscados.value.map((a) => ({ ...a, responsiva_estado: estadoResponsiva(a) }))
);

const activosFiltrados = computed(() => {
  const filtrados = activosParaFiltro.value.filter(
    (a) =>
      (!filtroCategoriaSet.value.size ||
        filtroCategoriaSet.value.has(a.categoria_nombre)) &&
      (!filtroResponsivaSet.value.size ||
        filtroResponsivaSet.value.has(a.responsiva_estado))
  );
  return [...filtrados].sort((a, b) => {
    let va = a[sortKey.value] ?? "";
    let vb = b[sortKey.value] ?? "";
    const cmp = String(va).localeCompare(String(vb), "es", {
      sensitivity: "base",
      numeric: true,
    });
    return sortDir.value === "asc" ? cmp : -cmp;
  });
});

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(activosFiltrados.value.length / porPagina.value))
);
const activosPaginados = computed(() => {
  const ini = (paginaActual.value - 1) * porPagina.value;
  return activosFiltrados.value.slice(ini, ini + porPagina.value);
});
const rangoInicio = computed(() =>
  activosFiltrados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, activosFiltrados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value,
    actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ord = [...set].sort((a, b) => a - b);
  const res = [];
  for (let i = 0; i < ord.length; i++) {
    if (i > 0 && ord[i] - ord[i - 1] > 1) res.push("...");
    res.push(ord[i]);
  }
  return res;
});

function cambiarPorPagina(n) {
  porPagina.value = n;
  paginaActual.value = 1;
}

// ── Helpers ──────────────────────────────────────────────────────
function formatCurrency(value) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(value);
}
function formatFecha(fecha) {
  if (!fecha) return "—";
  const [y, m, d] = fecha.split("-");
  return `${d}/${m}/${y}`;
}
function onResponsivaChange(e) {
  form.value.nueva_responsiva = e.target.files[0] ?? null;
}
function onFacturaChange(e) {
  form.value.nueva_factura = e.target.files[0] ?? null;
}

// ── Imágenes de evidencia — alta/edición ────────────────────
// No se sube/elimina nada del servidor aquí: solo se arma una cola local
// (imagenesNuevas / imagenesAEliminar) que se aplica recién en guardarActivo(),
// para no dejar imágenes huérfanas si el usuario cierra el modal sin guardar.
const fileRefImagen = ref(null);
const imagenesNuevas = ref([]); // [{ localId, file, previewUrl }]
const imagenesAEliminar = ref([]); // [id, id, ...] de imágenes existentes
const arrastrandoImagen = ref(false);

const imagenesVisibles = computed(() => {
  const existentes = (form.value.imagenes || [])
    .filter((img) => !imagenesAEliminar.value.includes(img.id))
    .map((img) => ({ tipo: "existente", key: `e-${img.id}`, id: img.id, src: img.imagen }));
  const nuevas = imagenesNuevas.value.map((p) => ({
    tipo: "nueva",
    key: `n-${p.localId}`,
    id: p.localId,
    src: p.previewUrl,
  }));
  return [...existentes, ...nuevas];
});

function agregarArchivosImagen(files) {
  for (const file of Array.from(files || [])) {
    if (!file.type.startsWith("image/")) continue;
    imagenesNuevas.value.push({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    });
  }
}

function handleSeleccionarImagenes(e) {
  agregarArchivosImagen(e.target.files);
  if (fileRefImagen.value) fileRefImagen.value.value = "";
}

function handleDropImagenes(e) {
  arrastrandoImagen.value = false;
  agregarArchivosImagen(e.dataTransfer?.files);
}

function eliminarMiniaturaImagen(item) {
  if (item.tipo === "nueva") {
    const pendiente = imagenesNuevas.value.find((p) => p.localId === item.id);
    if (pendiente) URL.revokeObjectURL(pendiente.previewUrl);
    imagenesNuevas.value = imagenesNuevas.value.filter((p) => p.localId !== item.id);
  } else {
    imagenesAEliminar.value = [...imagenesAEliminar.value, item.id];
  }
}

function limpiarImagenesPendientes() {
  imagenesNuevas.value.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  imagenesNuevas.value = [];
  imagenesAEliminar.value = [];
}

// ── API ──────────────────────────────────────────────────────────
async function cargarTodo() {
  loading.value = true;
  try {
    const [dataActivos, dataCategorias, dataAreas] = await Promise.all([
      getActivosFijos(),
      getCategoriasActivo(),
      getAreasEmpresa(),
    ]);
    activos.value = Array.isArray(dataActivos) ? dataActivos : dataActivos.results ?? [];
    categorias.value = Array.isArray(dataCategorias)
      ? dataCategorias
      : dataCategorias.results ?? [];
    areas.value = Array.isArray(dataAreas) ? dataAreas : dataAreas.results ?? [];
  } catch {
    Swal.fire("Error", "No se pudo cargar la información", "error");
  } finally {
    loading.value = false;
  }
}

async function exportarExcel() {
  try {
    await exportarActivosFijos();
  } catch {
    Swal.fire("Error", "No se pudo generar el archivo Excel.", "error");
  }
}

// ── Modal detalle ────────────────────────────────────────────────
async function abrirDetalle(activo) {
  detalleActivoRef.value = activo;
  detalle.value = null;
  detalleLoading.value = true;
  modalDetalleInstance.show();
  try {
    detalle.value = await getActivoFijo(activo.id);
  } catch {
    Swal.fire("Error", "No se pudo cargar el detalle", "error");
    modalDetalleInstance.hide();
  } finally {
    detalleLoading.value = false;
  }
}

// ── Modal crear / editar ─────────────────────────────────────────
async function abrirModal(activo) {
  limpiarImagenesPendientes();
  if (activo) {
    editando.value = true;
    form.value = {
      id: activo.id,
      articulo: activo.articulo,
      numero_serie: activo.numero_serie || "",
      categoria: activo.categoria,
      area: activo.area ?? null,
      responsable: activo.responsable || "",
      cantidad: activo.cantidad,
      costo_unitario: parseFloat(activo.costo_unitario),
      fecha_adquisicion: activo.fecha_adquisicion,
      notas: activo.notas || "",
      folio_fiscal: activo.folio_fiscal || "",
      responsiva_url: null,
      nueva_responsiva: null,
      reemplazarResponsiva: false,
      factura_url: null,
      nueva_factura: null,
      reemplazarFactura: false,
      imagenes: [],
      activo: activo.activo,
    };
    modalInstance.show();
    areaDropdownAbierto.value = false;
    try {
      const d = await getActivoFijo(activo.id);
      form.value.notas = d.notas || "";
      form.value.folio_fiscal = d.folio_fiscal || "";
      form.value.responsiva_url = d.responsiva_url || null;
      form.value.factura_url = d.factura_url || null;
      form.value.imagenes = d.imagenes || [];
    } catch {
      /* datos básicos ya están */
    }
  } else {
    editando.value = false;
    form.value = {
      id: null,
      articulo: "",
      numero_serie: "",
      categoria: "",
      area: null,
      responsable: "",
      cantidad: 1,
      costo_unitario: null,
      fecha_adquisicion: "",
      notas: "",
      folio_fiscal: "",
      responsiva_url: null,
      nueva_responsiva: null,
      reemplazarResponsiva: false,
      factura_url: null,
      nueva_factura: null,
      reemplazarFactura: false,
      imagenes: [],
      activo: true,
    };
    modalInstance.show();
  }
}

async function guardarActivo() {
  if (
    categoriaRequiereResponsiva.value &&
    !form.value.responsiva_url &&
    !form.value.nueva_responsiva
  ) {
    Swal.fire(
      "Responsiva requerida",
      `La categoría "${categoriaSeleccionada.value?.nombre}" requiere responsiva PDF.`,
      "warning"
    );
    return;
  }
  guardando.value = true;
  try {
    const fd = new FormData();
    fd.append("articulo", form.value.articulo.trim());
    fd.append("numero_serie", form.value.numero_serie?.trim() || "");
    fd.append("categoria", form.value.categoria);
    if (form.value.area) fd.append("area", form.value.area);
    fd.append("responsable", form.value.responsable?.trim() || "");
    fd.append("cantidad", form.value.cantidad);
    fd.append("costo_unitario", form.value.costo_unitario);
    fd.append("fecha_adquisicion", form.value.fecha_adquisicion);
    fd.append("notas", form.value.notas?.trim() || "");
    fd.append("folio_fiscal", form.value.folio_fiscal?.trim() || "");
    fd.append("activo", form.value.activo);
    if (form.value.nueva_responsiva) fd.append("responsiva", form.value.nueva_responsiva);
    if (form.value.nueva_factura) fd.append("factura", form.value.nueva_factura);

    let activoId = form.value.id;
    if (editando.value) {
      await actualizarActivoFijo(activoId, fd);
    } else {
      const creado = await crearActivoFijo(fd);
      activoId = creado.id;
    }

    // Las imágenes solo se sincronizan con el servidor hasta este punto,
    // una vez que el activo en sí ya se guardó correctamente.
    for (const imagenId of imagenesAEliminar.value) {
      await eliminarImagenActivoFijo(imagenId);
    }
    for (const pendiente of imagenesNuevas.value) {
      const fdImg = new FormData();
      fdImg.append("imagen", pendiente.file);
      await subirImagenActivoFijo(activoId, fdImg);
    }
    limpiarImagenesPendientes();

    await cargarTodo();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Activo ${editando.value ? "actualizado" : "registrado"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (err) {
    const data = err?.response?.data;
    const msg =
      data?.responsiva?.[0] ||
      data?.articulo?.[0] ||
      data?.numero_serie?.[0] ||
      data?.non_field_errors?.[0] ||
      "No se pudo guardar el activo";
    Swal.fire("Error", msg, "error");
  } finally {
    guardando.value = false;
  }
}

// ── Modal eliminar ───────────────────────────────────────────────
function confirmarEliminar(activo) {
  activoAEliminar.value = activo;
  motivoEliminar.value = "";
  modalEliminarInstance.show();
}
async function ejecutarEliminar() {
  if (!motivoEliminar.value.trim()) return;
  eliminando.value = true;
  try {
    await eliminarActivoFijo(activoAEliminar.value.id, motivoEliminar.value.trim());
    await cargarTodo();
    modalEliminarInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Activo eliminado",
      text: "El activo fue eliminado y registrado en el historial",
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo eliminar el activo", "error");
  } finally {
    eliminando.value = false;
  }
}

// ── Lifecycle ────────────────────────────────────────────────────
onMounted(() => {
  cargarTodo();
  modalInstance = new Modal(modalElement.value);
  modalEliminarInstance = new Modal(modalEliminarElement.value);
  modalDetalleInstance = new Modal(modalDetalleElement.value);
  modalImagenesInstance = new Modal(modalImagenesElement.value);
  modalElement.value.addEventListener("hidden.bs.modal", limpiarImagenesPendientes);
});
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-divisan {
  color: rgba(64, 34, 8, 0.6);
  font-weight: 500;
  font-size: 0.85rem;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-exportar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
}
.btn-exportar:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-cancelar {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  font-weight: 600;
  border-radius: 8px;
  padding: 0.5rem 1rem;
}
.btn-cancelar:hover {
  background: rgba(96, 108, 56, 0.08);
}
.btn-eliminar {
  background-color: #c0392b;
  color: white;
  border: none;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.5rem 1rem;
}
.btn-eliminar:hover:not(:disabled) {
  background-color: #a93226;
}
.btn-eliminar:disabled {
  opacity: 0.45;
  cursor: default;
}
.btn-reemplazar {
  background: transparent;
  border: 1px solid var(--d-green);
  color: var(--d-green);
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 6px;
  padding: 2px 10px;
  cursor: pointer;
  transition: 0.2s;
}
.btn-reemplazar:hover {
  background: rgba(96, 108, 56, 0.1);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: 15px;
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  padding: 12px 15px;
  font-weight: 800;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 10px 15px;
}
.badge-serie {
  background: rgba(96, 108, 56, 0.08);
  color: var(--d-green-dark);
  padding: 3px 8px;
  border-radius: 5px;
  font-family: var(--d-font-mono);
  font-size: 0.8rem;
  border: 1px solid rgba(96, 108, 56, 0.2);
}

.badge-categoria {
  color: var(--d-brown);
  padding: 4px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  display: inline-block;
  line-height: 1.4;
}

.badge-doc {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
}
.badge-doc.ok {
  background: var(--d-green);
  color: var(--d-cream);
}
.badge-doc.faltante {
  background: #c0392b;
  color: white;
}
.badge-doc.na {
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
}

.costo-total-display {
  background: rgba(96, 108, 56, 0.08);
  border: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-weight: 800;
  font-size: 1rem;
  color: var(--d-green-dark);
  font-family: var(--d-font-mono);
  text-align: right;
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.alerta-responsiva {
  background: rgba(96, 108, 56, 0.08);
  border: 1px solid rgba(96, 108, 56, 0.3);
  border-left: 4px solid var(--d-green);
  border-radius: 8px;
  padding: 0.65rem 1rem;
  color: var(--d-green-dark);
  font-size: 0.875rem;
  font-weight: 600;
}
.doc-actual {
  background: rgba(96, 108, 56, 0.06);
  border: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
}
.activo-eliminar-info {
  background: rgba(192, 57, 43, 0.06);
  border: 1px solid rgba(192, 57, 43, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}
.input-prefix {
  background-color: rgba(96, 108, 56, 0.1);
  border: 1px solid var(--d-green);
  border-right: none;
  color: var(--d-brown);
  font-weight: 700;
  border-radius: 8px 0 0 8px;
}

/* ── Columna artículo ── */
.col-articulo {
  width: 220px;
  min-width: 180px;
  max-width: 240px;
}
.col-articulo-td {
  width: 220px;
  min-width: 180px;
  max-width: 240px;
}

/* ── Columna artículo ── */
.articulo-texto {
  font-size: 0.82rem;
  display: block;
  word-break: break-word;
  white-space: normal;
  line-height: 1.3;
}

/* ── Detalle — tarjetas ── */
.det-card {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 10px;
  padding: 10px 14px;
}
.det-card-full {
  display: block;
}
.det-card-destacado {
  background: rgba(96, 108, 56, 0.06);
  border-color: rgba(96, 108, 56, 0.3);
}
.det-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.det-grid-4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.det-card-label {
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--d-green);
  margin-bottom: 4px;
  letter-spacing: 0.04em;
}
.det-card-valor {
  font-size: 0.9rem;
  color: var(--d-brown);
}
.folio-fiscal-text {
  font-family: var(--d-font-mono);
  font-size: 0.82rem;
  background: rgba(64, 34, 8, 0.05);
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(64, 34, 8, 0.1);
  word-break: break-all;
}
.docs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.doc-card {
  border-radius: 10px;
  padding: 0.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 3px;
  border: 1px solid transparent;
}
.doc-card.doc-ok {
  background: rgba(96, 108, 56, 0.08);
  border-color: rgba(96, 108, 56, 0.2);
}
.doc-card.doc-faltante {
  background: rgba(192, 57, 43, 0.06);
  border-color: rgba(192, 57, 43, 0.2);
}
.doc-card.doc-na {
  background: rgba(64, 34, 8, 0.04);
  border-color: rgba(64, 34, 8, 0.1);
}
.doc-icon {
  font-size: 1.25rem;
  color: var(--d-green);
}
.doc-titulo {
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--d-brown);
}
.btn-doc-link {
  background: var(--d-green);
  color: var(--d-cream);
  border: none;
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: 0.2s;
}
.btn-doc-link:hover {
  background: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-doc-link-plain {
  border: none;
  cursor: pointer;
}

/* ── Fotos de evidencia — dropzone (modal alta/edición) ───── */
.upload-zone-img {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px dashed var(--d-green);
  border-radius: 8px;
  background: rgba(96, 108, 56, 0.05);
  padding: 8px 12px;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-brown);
}
.upload-zone-img i {
  font-size: 1rem;
  color: var(--d-green);
}
.upload-zone-img small {
  font-weight: 500;
  color: var(--d-text-muted, #8a7a63);
}
.upload-zone-img:hover,
.upload-zone-img.is-dragover {
  border-color: var(--d-green-dark);
  background: rgba(40, 54, 24, 0.07);
}

/* ── Miniaturas de fotos (modal alta/edición) ─────────────── */
.miniaturas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 8px;
}
.miniatura-item {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
}
.miniatura-pendiente {
  border: 1.5px dashed var(--d-green);
}
.miniatura-badge {
  position: absolute;
  bottom: 3px;
  left: 3px;
  background: var(--d-green);
  color: white;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.6rem;
  font-weight: 800;
  text-transform: uppercase;
}
.miniatura-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.miniatura-eliminar {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}
.miniatura-eliminar:hover {
  background: #dc3545;
}

/* ── Móvil: carrusel horizontal en vez de cuadrícula ──────── */
@media (max-width: 768px) {
  .miniaturas-grid {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 8px;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
  }
  .miniatura-item {
    flex: 0 0 84px;
    width: 84px;
  }
}

/* ── Carrusel de fotos (modal detalle) ────────────────────── */
.carrusel-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.carrusel-viewport {
  position: relative;
  width: 100%;
  height: 320px;
  background: rgba(64, 34, 8, 0.05);
  border: 1.5px solid rgba(96, 108, 56, 0.2);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.carrusel-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.carrusel-flecha {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(64, 34, 8, 0.15);
  color: var(--d-brown);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.15s;
}
.carrusel-flecha:hover {
  background: var(--d-brown);
  color: white;
}
.carrusel-flecha-izq {
  left: 10px;
}
.carrusel-flecha-der {
  right: 10px;
}
.carrusel-footer {
  display: flex;
  align-items: center;
  justify-content: center;
}
.carrusel-contador {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  opacity: 0.7;
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
  cursor: pointer;
}
.btn-circular:hover:not(:disabled) {
  background: rgba(96, 108, 56, 0.12);
}
.btn-circular-sm {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.8rem;
}
.btn-circular-sm:hover {
  background: rgba(96, 108, 56, 0.1);
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}
.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Dropdown custom de área ── */
.area-dropdown-wrap {
  position: relative;
}
.area-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  user-select: none;
  min-height: 38px;
  background: white;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.area-dropdown-trigger.area-open {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 0.2rem rgba(96, 108, 56, 0.2);
}
.area-selected-text {
  font-size: 0.9rem;
  color: var(--d-brown);
  font-weight: 600;
}
.area-placeholder {
  font-size: 0.9rem;
  color: rgba(64, 34, 8, 0.4);
}
.area-dropdown-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  border-radius: 10px;
  z-index: 200;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
}
.area-dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--d-brown);
  font-weight: 500;
  transition: background 0.12s;
}
.area-dropdown-item:hover {
  background: rgba(96, 108, 56, 0.1);
}
.area-item-activa {
  background: rgba(96, 108, 56, 0.15);
  font-weight: 700;
}
.area-item-none {
  color: rgba(64, 34, 8, 0.5);
  font-style: italic;
}
.area-dropdown-empty {
  padding: 10px 12px;
  font-size: 0.82rem;
  color: rgba(64, 34, 8, 0.45);
  text-align: center;
}

/* ── Input con icono ── */
.input-icon-wrap {
  position: relative;
}
.input-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.95rem;
  z-index: 2;
  pointer-events: none;
}
.input-con-icon {
  padding-left: 32px;
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
  color: var(--d-brown);
}
.divisan-input-modal:focus {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 0.2rem rgba(96, 108, 56, 0.2);
}

@media (max-width: 991.98px) {
  :deep(.modal) {
    z-index: 1200;
  }
  :deep(.modal-backdrop) {
    z-index: 1199;
  }
  :deep(.modal-dialog) {
    margin-top: 75px;
  }
  .docs-grid {
    grid-template-columns: 1fr;
  }
  .det-grid-4 {
    grid-template-columns: 1fr 1fr;
  }
  .det-grid-2 {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
